import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../app/providers/AuthProvider";
import {
  getCurrentPersianDate,
  getCurrentPersianYearMonth,
  getPersianDateFromGregorian,
} from "../../../shared/utils/persianDate";
import { fmtDec, fmtInt, fmtNum } from "../../../shared/utils/formatters";
import {
  calendarTypeLabel,
  getCurrentGregorianDate,
  getGregorianDateFromDate,
  normalizeCalendarType,
  yearsForCalendar,
} from "../../../shared/utils/calendarType";
import {
  describeTargetWindow,
  hasPermission,
  isYearMonthInTargetWindow,
} from "../../../shared/permissions";
import {
  canProfileSetTargets,
  canProfileSetTargetsForCalendar,
} from "../../staff/api/transformers";
import { useGetTargetGroupsSetting } from "../../settings/api/react-query";
import { useGetManagedStaff } from "../../staff/api/react-query";
import { useGetJobs } from "../../reference/api/react-query";
import {
  useDeletePersonnelTargetMutation,
  useGetManagerTargetDistribution,
  useGetMonthCustomerTargets,
  useGetPersonnelTargets,
  useGetSuggestedTargets,
  useGetTargetCoverage,
  useSaveMonthCustomerTargetMutation,
  useSavePersonnelTargetMutation,
} from "../api/react-query";
import {
  buildPersonnelTargetPayload,
  generateDateRange,
  generateTargetUid,
  mapTargetGroupsForStaff,
} from "../api/transformers";
import {
  LoadingBlock,
  FormField,
  FormGrid,
  FormActions,
  DataTableCard,
  DataTableRow,
  DataTableCell,
  Button,
  Select,
  Input,
  Card,
  SectionHeader,
  Badge,
  Pagination,
} from "../../../shared/ui/composites";
import ManagerTargetDistributionPanel from "./ManagerTargetDistributionPanel";
import { matchesStaffListFilters } from "../utils/staffListFilters";
import {
  mergeCoverageRows,
  mergeDistributionPayloads,
  targetingStatsFromCoverage,
} from "../utils/distributionSummary";

const STAFF_TABLE_COLUMNS = [
  { key: "id", label: "شناسه" },
  { key: "name", label: "نام و نام خانوادگی" },
  { key: "national", label: "کد ملی" },
  { key: "code", label: "کد پرسنلی" },
  { key: "dc", label: "مرکز توزیع" },
  { key: "status", label: "وضعیت تارگت" },
  { key: "actions", label: "عملیات" },
];

const STAFF_PAGE_SIZE = 10;

const TARGETS_TABLE_COLUMNS = [
  { key: "group", label: "گروه کالا" },
  { key: "year", label: "سال" },
  { key: "month", label: "ماه" },
  { key: "unit", label: "واحد" },
  { key: "calendar", label: "تقویم" },
  { key: "sale", label: "تارگت فروش" },
  { key: "customer", label: "تارگت مشتری" },
  { key: "f_sale", label: "تارگت فروش f" },
  { key: "f_customer", label: "تارگت مشتری f" },
  { key: "actions", label: "عملیات" },
];

const currentPersianDate = getCurrentPersianYearMonth();
const todayPersian = getCurrentPersianDate();
const todayGregorian = getCurrentGregorianDate();

export default function TargetsTab() {
  const { profile, refetch: refetchProfile } = useAuth();

  useEffect(() => {
    refetchProfile();
  }, [refetchProfile]);
  // Direct reports (supervisor_id = me). job_id 77 / superuser: API returns all personnel.
  const { data: staff = [], isLoading: loading } = useGetManagedStaff();
  const { data: targetGroupsSetting = [] } = useGetTargetGroupsSetting();
  const { data: jobs = [] } = useGetJobs();
  const saveTarget = useSavePersonnelTargetMutation();
  const deleteTarget = useDeletePersonnelTargetMutation();

  const [selectedStaff, setSelectedStaff] = useState(null);

  const { data: existingTargets = [], isLoading: targetLoading } = useGetPersonnelTargets(
    selectedStaff?.personnel_id,
    !!selectedStaff,
  );

  const [showNewForm, setShowNewForm] = useState(false);

  const [targetForm, setTargetForm] = useState({
    uid: "",
    group_id: "",
    manufacturer_id: "",
    product_group_id: "",
    unit_type: "",
    from_date: "",
    to_date: "",
    year_number: String(currentPersianDate.year),
    month_number: String(currentPersianDate.month),
    sale_target: 0,
    customer_target: 0,
    f_sale_target: 0,
    f_customer_target: 0,
  });

  const [isEditMode, setIsEditMode] = useState(false);

  const [targetLoadingLocal, setTargetLoadingLocal] = useState(false);
  const isTargetBusy = targetLoading || targetLoadingLocal;

  const [selectedYearFilter, setSelectedYearFilter] = useState("");
  const [selectedMonthFilter, setSelectedMonthFilter] = useState("");
  const [monthGoalDrafts, setMonthGoalDrafts] = useState({ jalali: "", gregorian: "" });
  
  const [staffSearchQuery, setStaffSearchQuery] = useState("");
  const [completionFilter, setCompletionFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [staffPage, setStaffPage] = useState(1);

  const viewerLevel = parseInt(profile?.level_type, 10) || 0;
  const showDistributionPanel = viewerLevel >= 2;

  const specificPeriod =
    !!selectedStaff && !!selectedYearFilter && !!selectedMonthFilter;

  const { data: targetCoverage = [] } = useGetTargetCoverage(
    specificPeriod ? Number(selectedYearFilter) : currentPersianDate.year,
    specificPeriod ? Number(selectedMonthFilter) : currentPersianDate.month,
    true,
    "jalali",
  );

  const {
    data: distributionData,
    isLoading: distributionLoading,
  } = useGetManagerTargetDistribution(
    specificPeriod ? Number(selectedYearFilter) : currentPersianDate.year,
    specificPeriod ? Number(selectedMonthFilter) : currentPersianDate.month,
    showDistributionPanel,
    "jalali",
  );

  const distributionPeriodLabel = specificPeriod
    ? `${fmtInt(selectedYearFilter)}/${fmtInt(selectedMonthFilter)}`
    : `${fmtInt(currentPersianDate.year)}/${fmtInt(currentPersianDate.month)}`;

  const coverageByPersonnel = useMemo(() => {
    const map = {};
    targetCoverage.forEach((c) => {
      map[c.personnel_id] = c;
    });
    return map;
  }, [targetCoverage]);

  const goodsGroups = useMemo(
    () => mapTargetGroupsForStaff(targetGroupsSetting, selectedStaff),
    [targetGroupsSetting, selectedStaff],
  );

  // Live job catalog carries the current Jalali/Gregorian day windows.
  const jobDetails = useMemo(() => {
    const live =
      profile && jobs.length
        ? jobs.find((j) => String(j.id) === String(profile.job_id))
        : null;
    if (live) return live;
    return profile?.job_details || null;
  }, [profile, jobs]);

  const jalaliWindowLabel = useMemo(
    () => describeTargetWindow(jobDetails, "jalali"),
    [jobDetails],
  );
  const gregorianWindowLabel = useMemo(
    () => describeTargetWindow(jobDetails, "gregorian"),
    [jobDetails],
  );

  const canTargetStaff = canProfileSetTargets(profile);
  const canCreateJalali =
    canTargetStaff &&
    canProfileSetTargetsForCalendar(profile, "jalali") &&
    hasPermission(jobDetails, "targets.create");
  const canCreateGregorian =
    canTargetStaff &&
    canProfileSetTargetsForCalendar(profile, "gregorian") &&
    hasPermission(jobDetails, "targets.create");
  const canCreateTarget = canCreateJalali || canCreateGregorian;
  const canEditCalendar = (calendar) =>
    canTargetStaff &&
    canProfileSetTargetsForCalendar(profile, calendar) &&
    hasPermission(jobDetails, "targets.edit");
  const canDeleteCalendar = (calendar) =>
    canTargetStaff &&
    canProfileSetTargetsForCalendar(profile, calendar) &&
    hasPermission(jobDetails, "targets.delete");

  const isYearMonthAllowed = (year, month, rowCalendar = "jalali") =>
    isYearMonthInTargetWindow(
      jobDetails,
      year,
      month,
      normalizeCalendarType(rowCalendar) === "gregorian"
        ? todayGregorian
        : todayPersian,
      normalizeCalendarType(rowCalendar) === "gregorian"
        ? getGregorianDateFromDate
        : getPersianDateFromGregorian,
      rowCalendar,
    );

  // Filter staff by search, target completeness, and assigned product groups
  const filteredStaff = useMemo(() => {
    const query = staffSearchQuery.trim().toLowerCase();
    return staff.filter((s) => {
      if (query) {
        const nameMatch = (s.full_name || "").toLowerCase().includes(query);
        const codeMatch = (s.personnel_code || "").toLowerCase().includes(query);
        const nationalCodeMatch = (s.national_code || "").toLowerCase().includes(query);
        if (!nameMatch && !codeMatch && !nationalCodeMatch) return false;
      }
      return matchesStaffListFilters(s, coverageByPersonnel[s.personnel_id], {
        completion: completionFilter,
        group: groupFilter,
      });
    });
  }, [staff, staffSearchQuery, coverageByPersonnel, completionFilter, groupFilter]);

  const staffTotalPages = Math.max(1, Math.ceil(filteredStaff.length / STAFF_PAGE_SIZE));
  const pagedStaff = useMemo(() => {
    const page = Math.min(staffPage, staffTotalPages);
    const start = (page - 1) * STAFF_PAGE_SIZE;
    return filteredStaff.slice(start, start + STAFF_PAGE_SIZE);
  }, [filteredStaff, staffPage, staffTotalPages]);

  useEffect(() => {
    setStaffPage(1);
  }, [staffSearchQuery, completionFilter, groupFilter]);

  useEffect(() => {
    if (staffPage > staffTotalPages) setStaffPage(staffTotalPages);
  }, [staffPage, staffTotalPages]);

  const incompleteCount = useMemo(
    () => filteredStaff.filter((s) => coverageByPersonnel[s.personnel_id]?.is_complete === false).length,
    [filteredStaff, coverageByPersonnel],
  );

  const suggestedParams = {
    personnelId: selectedStaff?.personnel_id,
    manufacturerId: targetForm.manufacturer_id,
    productGroupId: targetForm.product_group_id,
    year: targetForm.year_number,
    month: targetForm.month_number,
  };

  const canFetchSuggested =
    showNewForm &&
    !!suggestedParams.manufacturerId &&
    !!suggestedParams.productGroupId &&
    !!suggestedParams.year &&
    !!suggestedParams.month &&
    !!selectedStaff;

  const { data: suggestedTargetsData, isLoading: suggestedLoading } = useGetSuggestedTargets(
    suggestedParams,
    canFetchSuggested,
  );

  const { data: monthGoals = [] } = useGetMonthCustomerTargets(
    selectedStaff?.personnel_id,
    selectedYearFilter,
    selectedMonthFilter,
    !!selectedStaff,
  );
  const saveMonthGoal = useSaveMonthCustomerTargetMutation();

  useEffect(() => {
    const jalali = monthGoals.find(
      (row) => normalizeCalendarType(row.calendar_type) === "jalali",
    );
    const gregorian = monthGoals.find(
      (row) => normalizeCalendarType(row.calendar_type) === "gregorian",
    );
    setMonthGoalDrafts({
      jalali: jalali != null ? String(jalali.total_customer_target ?? "") : "",
      gregorian: gregorian != null ? String(gregorian.total_customer_target ?? "") : "",
    });
  }, [monthGoals]);

  const years = useMemo(() => {
    return yearsForCalendar("jalali", currentPersianDate.year);
  }, []);
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const periodForCalendar = (calendar, yearFilter = selectedYearFilter, monthFilter = selectedMonthFilter) => {
    const normalized = normalizeCalendarType(calendar);
    const current = normalized === "gregorian" ? todayGregorian : currentPersianDate;
    const allowedYears = yearsForCalendar(normalized, currentPersianDate.year).map(String);
    const year = yearFilter && allowedYears.includes(String(yearFilter))
      ? String(yearFilter)
      : String(current.year);
    const month = monthFilter ? String(monthFilter) : String(current.month);
    return { year, month };
  };

  useEffect(() => {
    if (selectedStaff) {
      setSelectedYearFilter("");
      setSelectedMonthFilter("");

      setTargetForm({
        uid: "",
        group_id: "",
        manufacturer_id: "",
        product_group_id: "",
        unit_type: "",
        from_date: "",
        to_date: "",
        year_number: String(currentPersianDate.year),
        month_number: String(currentPersianDate.month),
        sale_target: 0,
        customer_target: 0,
        f_sale_target: 0,
        f_customer_target: 0,
      });
      setIsEditMode(false);
    }
  }, [selectedStaff]);

  useEffect(() => {
    // Auto-fill 3-month averages only when creating a new target.
    // Do not overwrite values when editing an existing row.
    if (!canFetchSuggested || !suggestedTargetsData || isEditMode) return;

    setTargetForm((current) => ({
      ...current,
      sale_target: suggestedTargetsData.sale_target,
      customer_target: suggestedTargetsData.customer_target,
      f_sale_target: suggestedTargetsData.f_sale_target || suggestedTargetsData.sale_target,
      f_customer_target: suggestedTargetsData.f_customer_target || suggestedTargetsData.customer_target,
    }));
  }, [canFetchSuggested, suggestedTargetsData, isEditMode]);

  const handleGroupSelect = (e) => {
    const selectedGroupId = e.target.value;

    if (!selectedGroupId) {
      setTargetForm({
        ...targetForm,
        group_id: "",
        manufacturer_id: "",
        product_group_id: "",
      });
      return;
    }

    const selectedGroup = goodsGroups.find(
      (group) => group.id === parseInt(selectedGroupId, 10),
    );

    if (selectedGroup) {
      console.log("Selected Group Details:", {
        group_id: selectedGroup.id,
        group_name: selectedGroup.name,
        manufacturer_id: selectedGroup.manufacturer_id,
        product_group_id: selectedGroup.product_group_id,
        unit: selectedGroup.unit,
      });

      const groupCalendar = normalizeCalendarType(selectedGroup.calendar_type);
      const period = periodForCalendar(groupCalendar);
      setTargetForm((prev) => ({
        ...prev,
        group_id: String(selectedGroup.id),
        manufacturer_id: selectedGroup.manufacturer_id,
        product_group_id: selectedGroup.product_group_id,
        unit_type: selectedGroup.unit,
        year_number: period.year,
        month_number: period.month,
      }));
    }
  };

  const handleTargetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return alert("کارمند انتخاب نشده است.");

    const {
      uid,
      manufacturer_id,
      product_group_id,
      unit_type,
      from_date,
      to_date,
      year_number,
      month_number,
      sale_target,
      customer_target,
      f_sale_target,
      f_customer_target,
    } = targetForm;

    const groupCalendar = getGoodsGroupCalendar(manufacturer_id, product_group_id);
    if (!canProfileSetTargetsForCalendar(profile, groupCalendar)) {
      alert(
        groupCalendar === "gregorian"
          ? "شما مجوز تارگت‌گذاری گروه‌های میلادی را ندارید"
          : "شما مجوز تارگت‌گذاری گروه‌های شمسی را ندارید",
      );
      return;
    }
    if (!isYearMonthAllowed(year_number, month_number, groupCalendar)) {
      alert(
        `این دوره خارج از پنجره مجاز ${calendarTypeLabel(groupCalendar)} است (${
          groupCalendar === "gregorian" ? gregorianWindowLabel : jalaliWindowLabel
        })`,
      );
      return;
    }

    try {
      setTargetLoadingLocal(true);
      const range = generateDateRange(
        parseInt(year_number, 10),
        parseInt(month_number, 10),
        groupCalendar,
      );

      const payload = buildPersonnelTargetPayload({
        uid: uid || generateTargetUid(
          selectedStaff.personnel_id,
          parseInt(manufacturer_id, 10),
          parseInt(product_group_id, 10),
          from_date || range.from_date,
          to_date || range.to_date,
        ),
        personnel_id: selectedStaff.personnel_id,
        manufacturer_id,
        product_group_id,
        unit_type,
        from_date: from_date || range.from_date,
        to_date: to_date || range.to_date,
        sale_target,
        customer_target,
        f_sale_target,
        f_customer_target,
        year_number,
        month_number,
        dc_id: selectedStaff.dc_id,
        level_type: selectedStaff.level_type,
        supervisor_id: selectedStaff.supervisor_id,
        saleoffice_id: Array.isArray(selectedStaff.saleoffice_ids)
          ? selectedStaff.saleoffice_ids[0]
          : null,
      });

      await saveTarget.mutateAsync(payload);
      alert(isEditMode ? "تارگت با موفقیت بروز شد." : "تارگت با موفقیت ذخیره شد.");

      setTargetForm({
        uid: "",
        group_id: "",
        manufacturer_id: "",
        product_group_id: "",
        unit_type: "",
        from_date: "",
        to_date: "",
        year_number: String(currentPersianDate.year),
        month_number: String(currentPersianDate.month),
        sale_target: 0,
        customer_target: 0,
        f_sale_target: 0,
        f_customer_target: 0,
      });
      setIsEditMode(false);
      setShowNewForm(false);
    } catch (err) {
      console.error("Error saving target:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "خطا در ذخیره تارگت. لطفاً دوباره تلاش کنید.",
      );
    } finally {
      setTargetLoadingLocal(false);
    }
  };

  const handleDeleteTarget = async (uid) => {
    if (!window.confirm("آیا مطمئن هستید که می‌خواهید این تارگت را حذف کنید?"))
      return;

    try {
      setTargetLoadingLocal(true);
      await deleteTarget.mutateAsync({ uid, personnelId: selectedStaff.personnel_id });
      alert("تارگت حذف شد.");
    } catch (err) {
      console.error("Error deleting target:", err);
      alert("خطا در حذف تارگت.");
    } finally {
      setTargetLoadingLocal(false);
    }
  };

  const handleEditTarget = (target) => {
    const selectedGroup = goodsGroups.find(
      (g) =>
        Number(g.manufacturer_id) === Number(target.manufacturer_id) &&
        Number(g.product_group_id) === Number(target.product_group_id),
    );

    const newForm = {
      uid: target.uid,
      group_id: selectedGroup ? String(selectedGroup.id) : "",
      manufacturer_id: String(target.manufacturer_id),
      product_group_id: String(target.product_group_id),
      unit_type: target.unit_type || selectedGroup?.unit || "",
      from_date: target.from_date || "",
      to_date: target.to_date || "",
      year_number: String(target.year_number),
      month_number: String(target.month_number),
      sale_target: target.sale_target ?? 0,
      customer_target: target.customer_target ?? 0,
      f_sale_target: target.f_sale_target ?? 0,
      f_customer_target: target.f_customer_target ?? 0,
    };

    setIsEditMode(true);
    setTargetForm(newForm);
    setShowNewForm(true);
  };

  const getGoodsGroupName = (manufacturerId, productGroupId) => {
    const found = goodsGroups.find(
      (g) =>
        g.manufacturer_id === parseInt(manufacturerId) &&
        g.product_group_id === parseInt(productGroupId),
    );
    return found?.name || productGroupId || "—";
  };

  const getGoodsGroupCalendar = (manufacturerId, productGroupId) => {
    const found = goodsGroups.find(
      (g) =>
        Number(g.manufacturer_id) === Number(manufacturerId) &&
        Number(g.product_group_id) === Number(productGroupId),
    );
    return normalizeCalendarType(found?.calendar_type);
  };

  const canEditTargetRow = (target) => {
    const calendar = getGoodsGroupCalendar(target.manufacturer_id, target.product_group_id);
    return (
      canEditCalendar(calendar) &&
      isYearMonthAllowed(target.year_number, target.month_number, calendar)
    );
  };

  const canDeleteTargetRow = (target) => {
    const calendar = getGoodsGroupCalendar(target.manufacturer_id, target.product_group_id);
    return (
      canDeleteCalendar(calendar) &&
      isYearMonthAllowed(target.year_number, target.month_number, calendar)
    );
  };

  const handleBackToList = () => {
    setSelectedStaff(null);
    setShowNewForm(false);
    setStaffSearchQuery("");
    setStaffPage(1);
  };

  const addFormYear = isEditMode ? targetForm.year_number : (selectedYearFilter || String(currentPersianDate.year));
  const addFormMonth = isEditMode ? targetForm.month_number : (selectedMonthFilter || String(currentPersianDate.month));

  useEffect(() => {
    if (!showNewForm || isEditMode || !targetForm.group_id) return;

    setTargetForm((current) => {
      if (
        String(current.year_number) === String(addFormYear) &&
        String(current.month_number) === String(addFormMonth)
      ) {
        return current;
      }
      return {
        ...current,
        year_number: String(addFormYear),
        month_number: String(addFormMonth),
      };
    });
  }, [showNewForm, isEditMode, addFormYear, addFormMonth, targetForm.group_id]);

  const filteredTargets = existingTargets.filter((t) => {
    const yearMatch =
      !selectedYearFilter || String(t.year_number) === selectedYearFilter;
    const monthMatch =
      !selectedMonthFilter || String(t.month_number) === selectedMonthFilter;
    return yearMatch && monthMatch;
  });

  // Exclude a group only when existingTargets already has the same
  // Manufacturer/Group + Year + Month combination as the form.
  // Keep the currently edited group so the select value stays valid.
  const formYear = String(targetForm.year_number || "");
  const formMonth = String(targetForm.month_number || "");
  const takenTargetKeys = new Set(
    existingTargets.map(
      (t) =>
        `${String(t.manufacturer_id)}-${String(t.product_group_id)}-${String(t.year_number)}-${String(t.month_number)}`,
    ),
  );
  const availableGoodsGroups = goodsGroups.filter((group) => {
    if (!canProfileSetTargetsForCalendar(profile, "jalali")) return false;
    if (!isYearMonthAllowed(formYear, formMonth, "jalali")) return false;
    const key = `${String(group.manufacturer_id)}-${String(group.product_group_id)}-${formYear}-${formMonth}`;
    const isTaken = takenTargetKeys.has(key);
    const isCurrentSelection =
      isEditMode && String(group.id) === String(targetForm.group_id);

    return !isTaken || isCurrentSelection;
  });

  return (
    <div className="p-4">
      {!selectedStaff ? (
        <>
          <SectionHeader title="انتخاب برای تنظیم تارگت" />

          {!canTargetStaff && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              حساب شما مجوز تارگت‌گذاری برای پرسنل را ندارد. این دسترسی را از تنظیمات پرسنل فعال کنید.
            </div>
          )}

          {showDistributionPanel && (
            <ManagerTargetDistributionPanel
              data={distributionData}
              isLoading={distributionLoading}
              showPeriodControls={false}
              periodLabel={distributionPeriodLabel}
            />
          )}

          {loading ? (
            <LoadingBlock />
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <Input
                  type="text"
                  placeholder="جستجو بر اساس نام یا کد..."
                  value={staffSearchQuery}
                  onChange={(e) => setStaffSearchQuery(e.target.value)}
                  className="w-full min-w-[220px] flex-1"
                />
                <Select
                  variant="filter"
                  aria-label="وضعیت تارگت"
                  className="w-auto min-w-[9.5rem]"
                  value={completionFilter}
                  onChange={(e) => setCompletionFilter(e.target.value)}
                >
                  <option value="all">همه وضعیت‌ها</option>
                  <option value="complete">کامل</option>
                  <option value="incomplete">ناقص</option>
                </Select>
                <Select
                  variant="filter"
                  aria-label="گروه کالا"
                  className="w-auto min-w-[9.5rem]"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                >
                  <option value="all">همه گروه‌ها</option>
                  <option value="with">دارای گروه</option>
                  <option value="without">بدون گروه</option>
                </Select>
                {incompleteCount > 0 && (
                  <span className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 whitespace-nowrap">
                    ⚠ {fmtInt(incompleteCount)} نفر تارگت ناقص دارند
                  </span>
                )}
              </div>
              <div className="overflow-hidden rounded-card border border-border">
                <DataTableCard
                  className="border-0 shadow-none rounded-none"
                  columns={STAFF_TABLE_COLUMNS}
                  isEmpty={filteredStaff.length === 0}
                  emptyMessage={
                    staffSearchQuery.trim() || completionFilter !== "all" || groupFilter !== "all"
                      ? "نتیجه‌ای یافت نشد"
                      : "داده‌ای موجود نیست"
                  }
                >
                  {pagedStaff.map((sm) => {
                    const coverage = coverageByPersonnel[sm.personnel_id];
                    const incomplete = coverage && coverage.is_complete === false;
                    return (
                      <DataTableRow
                        key={sm.personnel_id}
                        className={incomplete ? "bg-amber-50/80" : undefined}
                      >
                        <DataTableCell>{sm.personnel_id}</DataTableCell>
                        <DataTableCell>
                          <div className="font-semibold text-[#1e293b]">{sm.full_name}</div>
                          {incomplete && (
                            <div className="mt-0.5 text-xs font-medium text-amber-700">
                              تارگت کامل نیست ({coverage.set_count}/{coverage.required_count})
                            </div>
                          )}
                        </DataTableCell>
                        <DataTableCell>{sm.national_code}</DataTableCell>
                        <DataTableCell>{sm.personnel_code}</DataTableCell>
                        <DataTableCell>{sm.dc_name}</DataTableCell>
                        <DataTableCell>
                          {coverage == null ? (
                            <span className="text-xs text-[#94a3b8]">—</span>
                          ) : coverage.required_count === 0 ? (
                            <Badge variant="scope">بدون گروه کالا</Badge>
                          ) : incomplete ? (
                            <span className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">
                              ⚠ ناقص
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
                              ✓ کامل
                            </span>
                          )}
                        </DataTableCell>
                        <DataTableCell>
                          <Button
                            type="button"
                            variant="submit"
                            size="sm"
                            onClick={() => setSelectedStaff(sm)}
                          >
                            📊 تنظیم تارگت
                          </Button>
                        </DataTableCell>
                      </DataTableRow>
                    );
                  })}
                </DataTableCard>

                <Pagination
                  page={Math.min(staffPage, staffTotalPages)}
                  pageCount={staffTotalPages}
                  onChange={setStaffPage}
                  summary={
                    <>
                      نمایش{" "}
                      {filteredStaff.length === 0
                        ? fmtInt(0)
                        : fmtInt(
                            (Math.min(staffPage, staffTotalPages) - 1) *
                              STAFF_PAGE_SIZE +
                              1,
                          )}
                      –
                      {fmtInt(
                        Math.min(
                          Math.min(staffPage, staffTotalPages) * STAFF_PAGE_SIZE,
                          filteredStaff.length,
                        ),
                      )}{" "}
                      از {fmtInt(filteredStaff.length)} نفر
                    </>
                  }
                />
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="mb-8 flex items-center justify-between gap-4 border-b-2 border-[#e5e7eb] pb-4 max-md:flex-col max-md:items-stretch">
            <Button type="button" variant="cancel" onClick={handleBackToList}>
              ← بازگشت به لیست کارمندان
            </Button>
            <h2 className="m-0 text-xl font-extrabold text-slate-800 max-md:text-center">
              تنظیم تارگت: {selectedStaff.full_name}
            </h2>
            <div className="flex flex-col items-end gap-1">
              <Button
                type="button"
                variant="submit"
                onClick={() => {
                  if (showNewForm) {
                    setShowNewForm(false);
                  } else {
                    setIsEditMode(false);
                    const year = selectedYearFilter || String(currentPersianDate.year);
                    const month = selectedMonthFilter || String(currentPersianDate.month);
                    setTargetForm({
                      uid: "",
                      group_id: "",
                      manufacturer_id: "",
                      product_group_id: "",
                      unit_type: "",
                      from_date: "",
                      to_date: "",
                      year_number: year,
                      month_number: month,
                      sale_target: 0,
                      customer_target: 0,
                      f_sale_target: 0,
                      f_customer_target: 0,
                    });
                    setShowNewForm(true);
                  }
                }}
                disabled={!canCreateTarget}
                title={!canCreateTarget ? "مجوز ایجاد تارگت ندارید" : ""}
              >
                {showNewForm ? "❌ بستن فرم" : "➕ تارگت جدید"}
              </Button>
              {!canCreateTarget && (
                <span className="text-xs text-red-500 font-medium">
                  مجوز ایجاد تارگت برای نقش شما فعال نیست
                </span>
              )}
              {canCreateTarget && (
                <span className="text-xs text-slate-500 font-medium">
                  شمسی: {jalaliWindowLabel} · میلادی: {gregorianWindowLabel}
                </span>
              )}
            </div>
          </div>

          {showDistributionPanel && (
            <ManagerTargetDistributionPanel
              data={distributionData}
              isLoading={distributionLoading}
              showPeriodControls={false}
              periodLabel={distributionPeriodLabel}
            />
          )}

          {showNewForm && targetForm && (isEditMode === false || targetForm.uid) && (
            <form key={isEditMode ? `edit-${targetForm.uid}` : "new"} onSubmit={handleTargetSubmit}>
              <Card variant="form" padding="lg" className="mb-8">
                <div className="mb-3.5 space-y-2 rounded-control border border-dashed border-[#cfcfcf] bg-[#fafafa] px-3.5 py-3">
                  <div className="flex flex-wrap items-center gap-3.5">
                    <strong className="ml-1.5">پیشنهاد تارگت:</strong>
                    {suggestedLoading ? (
                      <span className="text-sm text-[#64748b]">در حال محاسبه...</span>
                    ) : (
                      <>
                        <Badge variant="scope">
                          فروش:{" "}
                          {suggestedTargetsData?.sale_target !== ""
                            ? fmtDec(suggestedTargetsData?.sale_target, 2)
                            : "—"}
                        </Badge>
                        <Badge variant="scope">
                          مشتری:{" "}
                          {suggestedTargetsData?.customer_target !== ""
                            ? fmtNum(suggestedTargetsData?.customer_target, 2)
                            : "—"}
                        </Badge>
                        {suggestedTargetsData?.unit_type && (
                          <Badge variant="scope">
                            واحد: {suggestedTargetsData.unit_type}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  {!suggestedLoading && suggestedTargetsData?.tip_formula && (
                    <p className="m-0 text-xs text-[#64748b] ltr text-left font-mono truncate" title={suggestedTargetsData.tip_formula}>
                      {suggestedTargetsData.tip_formula}
                    </p>
                  )}
                  {!suggestedLoading && suggestedTargetsData?.tip && (
                    <p className="m-0 text-xs text-amber-700">💡 {suggestedTargetsData.tip}</p>
                  )}
                </div>

                <h3 className="mb-6 text-lg font-semibold text-[#374151]">
                  {isEditMode ? "ویرایش تارگت" : "افزودن تارگت جدید"}
                </h3>

                <FormGrid columns="three">
                  <FormField label="تولید کننده - گروه" required>
                    <Select
                      variant="default"
                      value={targetForm.group_id}
                      onChange={handleGroupSelect}
                      required
                    >
                      <option value="">انتخاب کنید</option>
                      {availableGoodsGroups.length > 0 ? (
                        availableGoodsGroups.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} [{calendarTypeLabel(m.calendar_type)}] [{m.unit}]
                          </option>
                        ))
                      ) : (
                        <option disabled>گروهی یافت نشد</option>
                      )}
                    </Select>
                  </FormField>

                  <FormField label="سال" required>
                    <Select
                      variant="default"
                      value={
                        isEditMode ? targetForm.year_number : addFormYear
                      }
                      onChange={(e) =>
                        setTargetForm({
                          ...targetForm,
                          year_number: e.target.value,
                        })
                      }
                      required={isEditMode}
                      disabled={!isEditMode}
                      title={
                        !isEditMode
                          ? "این مقدار از فیلتر سال بالای جدول گرفته می‌شود"
                          : undefined
                      }
                    >
                      <option value="">انتخاب سال</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {fmtInt(year)}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label="ماه" required>
                    <Select
                      variant="default"
                      value={
                        isEditMode ? targetForm.month_number : addFormMonth
                      }
                      onChange={(e) =>
                        setTargetForm({
                          ...targetForm,
                          month_number: e.target.value,
                        })
                      }
                      required={isEditMode}
                      disabled={!isEditMode}
                      title={
                        !isEditMode
                          ? "این مقدار از فیلتر ماه بالای جدول گرفته می‌شود"
                          : undefined
                      }
                    >
                      <option value="">انتخاب ماه</option>
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {fmtInt(month)}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                </FormGrid>

                <FormGrid columns="two">
                  <FormField label="f تارگت فروش" required>
                    <Input
                      variant="form"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="مثال: 25.22"
                      value={targetForm.f_sale_target}
                      onChange={(e) =>
                        setTargetForm({
                          ...targetForm,
                          f_sale_target: e.target.value,
                        })
                      }
                      required
                    />
                  </FormField>
                  <FormField label="f تارگت مشتری" required>
                    <Input
                      variant="form"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="مثال: 150.25"
                      value={targetForm.f_customer_target}
                      onChange={(e) =>
                        setTargetForm({
                          ...targetForm,
                          f_customer_target: e.target.value,
                        })
                      }
                      required
                    />
                  </FormField>
                </FormGrid>

                <FormGrid columns="two">
                  <FormField label="تارگت فروش" required>
                    <Input
                      variant="form"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="مثال: 25.22"
                      value={targetForm.sale_target}
                      onChange={(e) =>
                        setTargetForm({
                          ...targetForm,
                          sale_target: e.target.value,
                        })
                      }
                      required
                    />
                  </FormField>
                  <FormField label="تارگت مشتری" required>
                    <Input
                      variant="form"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="مثال: 150.25"
                      value={targetForm.customer_target}
                      onChange={(e) =>
                        setTargetForm({
                          ...targetForm,
                          customer_target: e.target.value,
                        })
                      }
                      required
                    />
                  </FormField>
                </FormGrid>

                <FormActions>
                  <Button
                    type="button"
                    variant="cancel"
                    onClick={() => setShowNewForm(false)}
                    disabled={isTargetBusy}
                  >
                    انصراف
                  </Button>
                  <Button type="submit" variant="submit" disabled={isTargetBusy}>
                    {isTargetBusy ? "در حال ذخیره…" : "ذخیره تارگت"}
                  </Button>
                </FormActions>
              </Card>
            </form>
          )}

          <div className="mt-4">
            <SectionHeader
              title={`تارگت‌های ثبت‌شده (${fmtInt(filteredTargets.length)} از ${fmtInt(existingTargets.length)})`}
              className="mb-4"
            />

            <div className="mb-4 flex items-end gap-2 whitespace-nowrap">
              <FormField label="سال (شمسی)" layout="inline" className="w-32 shrink-0">
                <Select
                  variant="filter"
                  className="w-32"
                  value={selectedYearFilter}
                  onChange={(e) => setSelectedYearFilter(e.target.value)}
                >
                  <option value="">همه سال‌ها</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {fmtInt(year)}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="ماه (شمسی)" layout="inline" className="w-24 shrink-0">
                <Select
                  variant="filter"
                  className="w-24"
                  value={selectedMonthFilter}
                  onChange={(e) => setSelectedMonthFilter(e.target.value)}
                >
                  <option value="">همه ماه‌ها</option>
                  {months.map((month) => (
                    <option key={month} value={month}>
                      {fmtInt(month)}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>

            {selectedYearFilter && selectedMonthFilter && selectedStaff ? (
              <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <FormField label={`هدف کل مشتری (${fmtInt(selectedYearFilter)}/${fmtInt(selectedMonthFilter)})`} layout="inline" className="flex-1">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={monthGoalDrafts.jalali}
                    onChange={(e) =>
                      setMonthGoalDrafts((current) => ({
                        ...current,
                        jalali: e.target.value,
                      }))
                    }
                  />
                </FormField>
                <Button
                  type="button"
                  variant="submit"
                  size="sm"
                  disabled={saveMonthGoal.isPending}
                  onClick={async () => {
                    try {
                      await saveMonthGoal.mutateAsync({
                        personnel_id: selectedStaff.personnel_id,
                        year_number: Number(selectedYearFilter),
                        month_number: Number(selectedMonthFilter),
                        calendar_type: "jalali",
                        total_customer_target: Number(monthGoalDrafts.jalali || 0),
                      });
                      alert("هدف کل مشتری ماه ذخیره شد.");
                    } catch (err) {
                      alert(
                        err.response?.data?.message ||
                          err.response?.data?.error ||
                          "خطا در ذخیره هدف کل مشتری",
                      );
                    }
                  }}
                >
                  ذخیره هدف ماه
                </Button>
              </div>
            ) : (
              <p className="mb-4 text-xs text-slate-500">
                برای ثبت هدف کل مشتری این ماه، پرسنل و سال و ماه را انتخاب کنید.
              </p>
            )}

            {isTargetBusy && !showNewForm ? (
              <LoadingBlock />
            ) : (
              <DataTableCard
                columns={TARGETS_TABLE_COLUMNS}
                isEmpty={filteredTargets.length === 0}
                emptyMessage="هیچ تارگتی برای این فیلترها ثبت نشده است."
                tableClassName="rtl"
              >
             
                {filteredTargets.map((t) => (
                  <DataTableRow key={t.uid}>
                    <DataTableCell>
                      {getGoodsGroupName(
                        t.manufacturer_id,
                        t.product_group_id,
                      )}
                    </DataTableCell>
                    <DataTableCell>{t.year_number != null ? fmtInt(t.year_number) : "—"}</DataTableCell>
                    <DataTableCell>{t.month_number != null ? fmtInt(t.month_number) : "—"}</DataTableCell>
                    <DataTableCell>{t.unit_type || "—"}</DataTableCell>
                    <DataTableCell>
                      {calendarTypeLabel(
                        goodsGroups.find(
                          (g) =>
                            Number(g.manufacturer_id) === Number(t.manufacturer_id) &&
                            Number(g.product_group_id) === Number(t.product_group_id),
                        )?.calendar_type,
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {fmtDec(t.sale_target, 2)}
                    </DataTableCell>
                    <DataTableCell>
                      {fmtNum(t.customer_target, 2)}
                    </DataTableCell>
                    <DataTableCell>
                      {fmtDec(t.f_sale_target, 2)}
                    </DataTableCell>
                    <DataTableCell>
                      {fmtNum(t.f_customer_target, 2)}
                    </DataTableCell>
                    <DataTableCell>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="submit"
                          size="sm"
                          onClick={() => handleEditTarget(t)}
                          disabled={isTargetBusy || !canEditTargetRow(t)}
                          title={
                            canEditTargetRow(t)
                              ? ""
                              : "این تارگت خارج از مجوز یا پنجره تقویم گروه کالا است"
                          }
                        >
                          ✎ ویرایش
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteTarget(t.uid)}
                          disabled={isTargetBusy || !canDeleteTargetRow(t)}
                          title={
                            canDeleteTargetRow(t)
                              ? ""
                              : "این تارگت خارج از مجوز یا پنجره تقویم گروه کالا است"
                          }
                        >
                          🗑️ حذف
                        </Button>
                      </div>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableCard>
            )}
          </div>
        </>
      )}
    </div>
  );
}
